import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useDownloadExport, useExportManifest } from "../api/use-export";
import type { ExportManifestDto, ExportType } from "../types";
import { ExportCard } from "./export-card";

interface ExportModalProps {
  scheduleId: string;
  /** Latest run id — parent decides this; modal does not fetch the runs list. */
  runId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface CardSpec {
  type: ExportType;
  title: string;
  description: string;
}

/**
 * Static card content for the modal. Order is the order Abbie evaluated
 * during the prototype walkthrough (overall first, then bundles by audience).
 * Keeping the array literal here means the modal renders exactly 4 cards;
 * per-room visibility is gated by the manifest flag at render time.
 */
const CARD_SPECS: readonly CardSpec[] = [
  {
    type: "overall",
    title: "Overall schedule",
    description:
      "Single ICS file with every section (as a recurring series) and the assigned students as attendees.",
  },
  {
    type: "overall-split",
    title: "Overall schedule (split)",
    description:
      "Same calendar as a zip of ~1 MB .ics files — use if a single file is too large for Outlook to import.",
  },
  {
    type: "per-teacher",
    title: "Per-teacher bundle",
    description:
      "Zip with one .ics per teacher — only their events, with their students listed.",
  },
  {
    type: "per-student",
    title: "Per-student bundle",
    description:
      "Zip with one .ics per student — only the sections they're assigned to.",
  },
  {
    type: "per-room",
    title: "Per-room bundle",
    description:
      "Zip with one .ics per room — useful for room-utilization reporting.",
  },
] as const;

/**
 * Looks up whether a given export type is enabled in the manifest. Pure
 * lookup table so the JSX stays declarative and exhaustiveness is checked
 * by the `ExportType` literal union.
 */
function isTypeEnabled(manifest: ExportManifestDto, type: ExportType): boolean {
  switch (type) {
    case "overall":
    case "overall-split":
      // Same run gating as the single overall export.
      return manifest.overallEnabled;
    case "per-teacher":
      return manifest.perTeacherEnabled;
    case "per-student":
      return manifest.perStudentEnabled;
    case "per-room":
      return manifest.perRoomEnabled;
  }
}

/**
 * Centered Phase 6 export modal. Lifecycle and a11y wiring mirrors
 * `reassign-modal.tsx` exactly:
 *
 *   - Native `<dialog>` element drives focus trap, ARIA semantics, and
 *     backdrop rendering.
 *   - The `isOpen` prop syncs to `dialog.showModal()` / `dialog.close()`.
 *   - The previously-focused element is captured on open and restored on
 *     close (next tick — same `setTimeout(0)` as the reassign modal).
 *   - ESC handled via both the dialog `cancel` event AND a document `keydown`
 *     listener (jsdom doesn't route `cancel` reliably).
 *   - Initial focus moves to the first enabled card once the manifest loads.
 *
 * Slice 4 stubs the per-card downloads (`console.log` no-op) — Slice 5 wires
 * the actual `useDownloadOverall` / `useDownloadPerTeacher` / etc. mutations.
 */
export function ExportModal({
  scheduleId,
  runId,
  isOpen,
  onClose,
}: ExportModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const firstOptionRef = useRef<HTMLButtonElement | null>(null);
  // Tracks whether we've already focused the first card for this open
  // session, so a refetch (or a re-render that re-renders the same cards)
  // doesn't steal focus mid-keyboard-navigation. Mirror of
  // `reassign-modal.tsx:hasFocusedFirstRef`.
  const hasFocusedFirstRef = useRef(false);

  const manifestQuery = useExportManifest(scheduleId, runId, {
    enabled: isOpen,
  });

  // One mutation instance per export type so each card can show its own
  // `isPending` spinner and error message independently — supports concurrent
  // downloads (D19: modal stays open, other cards remain clickable while one
  // is in flight). Rules-of-hooks are satisfied: each `useDownloadExport`
  // call is at the top level of the component, called in the same order on
  // every render.
  const overallMutation = useDownloadExport(scheduleId, runId, "overall");
  const overallSplitMutation = useDownloadExport(
    scheduleId,
    runId,
    "overall-split",
  );
  const perTeacherMutation = useDownloadExport(
    scheduleId,
    runId,
    "per-teacher",
  );
  const perStudentMutation = useDownloadExport(
    scheduleId,
    runId,
    "per-student",
  );
  const perRoomMutation = useDownloadExport(scheduleId, runId, "per-room");

  const mutationsByType = {
    overall: overallMutation,
    "overall-split": overallSplitMutation,
    "per-teacher": perTeacherMutation,
    "per-student": perStudentMutation,
    "per-room": perRoomMutation,
  } satisfies Record<ExportType, ReturnType<typeof useDownloadExport>>;

  // Per-card error message — populated by each mutation's onError, cleared
  // when the user retries the same card. Mirrors the Phase 5 reassign-modal
  // pattern of carrying error state in component state (vs. relying on
  // TanStack's `mutation.error`) so we can clear it on retry without
  // resetting other mutation state.
  const [errorsByType, setErrorsByType] = useState<
    Record<ExportType, string | null>
  >({
    overall: null,
    "overall-split": null,
    "per-teacher": null,
    "per-student": null,
    "per-room": null,
  });

  // Organizer override for the overall (master BSC calendar) exports. Defaults
  // to the signed-in user's email so the person running the export owns the
  // imported events and can "Send Updates" to every attendee in Outlook (the
  // room resource the calendar used has no login). Cleared → the backend falls
  // back to the configured BSC shared mailbox.
  const { user } = useAuth();
  const [organizer, setOrganizer] = useState(user?.email ?? "");

  // Sync the native dialog's open state with the `isOpen` prop. Capture the
  // previously-focused element BEFORE showing so we can restore on close.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      lastFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      hasFocusedFirstRef.current = false;
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Restore focus to the trigger after the dialog closes. The `close` event
  // fires both when ESC dismisses the dialog AND when our prop-sync effect
  // calls `dialog.close()`; either way focus should land back on the trigger.
  // setTimeout(0) so the focus call runs after React unmount work.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      window.setTimeout(() => {
        const prev = lastFocusedRef.current;
        if (prev && typeof prev.focus === "function") {
          prev.focus();
        }
        lastFocusedRef.current = null;
      }, 0);
    };
    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, []);

  // ESC / cancel handling. Native `<dialog>` fires a `cancel` event on ESC;
  // jsdom doesn't route it reliably, so we also listen for the key directly.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dialog.open) {
        e.preventDefault();
        onClose();
      }
    };
    dialog.addEventListener("cancel", handleCancel);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Initial focus: once the manifest first resolves, move focus to the first
  // card's Download button so the user can pick via keyboard without a Tab.
  // Skipped on loading / error states (those have their own focusable
  // elements — Retry button or the Close header button is the fallback).
  useEffect(() => {
    if (!isOpen) return;
    if (!manifestQuery.data) return;
    if (hasFocusedFirstRef.current) return;
    const first = firstOptionRef.current;
    if (first) {
      first.focus();
      hasFocusedFirstRef.current = true;
    }
  }, [isOpen, manifestQuery.data]);

  // Backdrop click — clicking the dialog element itself (not a descendant)
  // means the click landed on the browser-rendered backdrop.
  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      onClose();
    }
  };

  // Click handler: clear any prior error for this card, fire the per-type
  // download mutation, and route the error onto the card's red error line on
  // failure. Per D19 the modal stays open after success — no `onClose` here.
  const handleCardClick = (type: ExportType) => {
    setErrorsByType((prev) => ({ ...prev, [type]: null }));
    mutationsByType[type].mutate(
      { organizer },
      {
        onError: (err) => {
          const message =
            err instanceof Error ? err.message : "Download failed";
          setErrorsByType((prev) => ({ ...prev, [type]: message }));
        },
      },
    );
  };

  const visibleCards = manifestQuery.data
    ? CARD_SPECS.filter((card) => {
        // Per-room is the only currently-hideable card: when the feature flag
        // says off, drop the card entirely (D10 in the Phase 6 design). The
        // other three cards always render; their `enabled` prop comes from
        // the manifest in case they ever become flaggable.
        if (card.type === "per-room" && !manifestQuery.data!.perRoomEnabled) {
          return false;
        }
        return true;
      })
    : [];

  const body = manifestQuery.isLoading ? (
    <div
      data-testid="export-modal-loading"
      className="py-10 flex flex-col items-center gap-3"
    >
      <LoadingSpinner size="lg" />
      <span className="text-[13px] text-text-muted">
        Loading export options…
      </span>
    </div>
  ) : manifestQuery.isError ? (
    <ExportManifestError
      message={
        manifestQuery.error instanceof Error
          ? manifestQuery.error.message
          : "Failed to load export options"
      }
      onRetry={() => manifestQuery.refetch()}
    />
  ) : manifestQuery.data ? (
    <>
      <div className="mb-4">
        <label
          htmlFor="export-organizer"
          className="block text-[13px] font-medium text-text-soft"
        >
          Organizer for the overall calendar
        </label>
        <input
          id="export-organizer"
          type="email"
          inputMode="email"
          autoComplete="off"
          data-testid="export-organizer-input"
          value={organizer}
          onChange={(e) => setOrganizer(e.target.value)}
          placeholder="bsccalendar@Butlertech.org"
          className="mt-1.5 w-full rounded-md border border-border-subtle bg-page px-3 py-2 text-[13px] text-white placeholder:text-text-muted focus:border-brand focus:outline-none"
        />
        <p className="mt-1.5 text-[12px] text-text-muted">
          The mailbox that owns the imported events and can “Send Updates” to
          attendees in Outlook. Leave blank to use the default BSC calendar
          mailbox. Applies to the two overall exports only.
        </p>
      </div>
      <div data-testid="export-modal-cards" className="flex flex-col gap-2">
        {visibleCards.map((card, idx) => (
          <ExportCard
            key={card.type}
            type={card.type}
            title={card.title}
            description={card.description}
            enabled={isTypeEnabled(manifestQuery.data!, card.type)}
            isPending={mutationsByType[card.type].isPending}
            error={errorsByType[card.type]}
            onClick={handleCardClick}
            buttonRef={idx === 0 ? firstOptionRef : undefined}
          />
        ))}
      </div>
    </>
  ) : null;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="export-modal-title"
      aria-modal="true"
      data-testid="export-modal"
      onClick={handleBackdropClick}
      className="m-auto bg-surface border border-border-subtle rounded-lg w-full max-w-[500px] max-h-[86vh] p-0 text-text-soft animate-fade-in [&::backdrop]:bg-black/50"
    >
      <div className="flex flex-col max-h-[86vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-[18px] border-b border-border-subtle">
          <div className="min-w-0">
            <h3
              id="export-modal-title"
              data-testid="export-modal-title"
              className="m-0 text-[17px] font-semibold text-white flex items-center gap-2"
            >
              <span>Export schedule</span>
              {manifestQuery.data && (
                <span
                  data-testid="export-modal-run-pill"
                  className="bg-surface-2 text-text-muted rounded-full px-2 py-[2px] text-[11px] font-medium"
                >
                  Run #{manifestQuery.data.runNumber}
                </span>
              )}
            </h3>
            <p
              data-testid="export-modal-subhead"
              className="mt-[3px] text-[13px] text-text-muted"
            >
              Generate ICS files to import back into Outlook with students
              attached.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close export modal"
            data-testid="export-modal-close"
            className="text-text-muted hover:text-white text-[20px] leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">{body}</div>
      </div>
    </dialog>
  );
}

interface ExportManifestErrorProps {
  message: string;
  onRetry: () => void;
}

function ExportManifestError({ message, onRetry }: ExportManifestErrorProps) {
  return (
    <div
      data-testid="export-modal-error"
      className="bg-page border border-red-500/30 rounded-md p-4 text-[13px] text-red-400 flex items-center justify-between gap-3"
    >
      <span>Failed to load export options{message ? `: ${message}` : ""}</span>
      <button
        type="button"
        onClick={onRetry}
        data-testid="export-modal-retry"
        className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-3 py-1.5 text-[12px] font-medium"
      >
        Retry
      </button>
    </div>
  );
}
